import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
interface FormData {
  name: string;
  phone: string;
  email: string;
  businessType: string;
  budget: string;
  message: string;
}
export function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    businessType: '',
    budget: '',
    message: ''
  });
  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your full name');
      return false;
    }
    if (!formData.phone.trim()) {
      setErrorMessage('Please enter your phone number');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    if (!formData.businessType) {
      setErrorMessage('Please select a business type');
      return false;
    }
    if (!formData.budget) {
      setErrorMessage('Please select an investment budget');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Anti-spam honeypot check
    if (honeypot) {
      console.log('Spam detected, aborting submission');
      return;
    }
    // Validate form
    if (!validateForm()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMessage('');
    // Hardcoded credentials (Magic Patterns doesn't support .env the same way)
    const serviceId = 'service_rjoubul9042';
    const templateId = 'template_rhmi35x';
    const publicKey = 'RGCZl84-qUlqPyTS7';
    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        business_type: formData.businessType,
        budget: formData.budget,
        message: formData.message || 'No additional message provided',
        to_name: 'Dubai Beauty Advisory Team'
      };
      console.log('Sending email with params:', templateParams);
      const response = await emailjs.send(serviceId, templateId, templateParams, publicKey);
      console.log('EmailJS response:', response);
      setStatus('success');
      // Clear form
      setFormData({
        name: '',
        phone: '',
        email: '',
        businessType: '',
        budget: '',
        message: ''
      });
      if (formRef.current) {
        formRef.current.reset();
      }
    } catch (error: any) {
      console.error('EmailJS error:', error);
      console.error('Error details:', error.text || error.message);
      setStatus('error');
      setErrorMessage(error.text || error.message || 'Failed to send your request. Please try again or contact us via WhatsApp.');
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };
  return <section id="contact-form" className="py-24 bg-cream relative overflow-hidden" data-id="element-37">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-charcoal hidden lg:block" data-id="element-38"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-39">
        <div className="grid lg:grid-cols-2 gap-16" data-id="element-40">
          {/* Left Content */}
          <div className="py-12 lg:pr-12" data-id="element-41">
            <h2 className="text-sm font-sans font-semibold tracking-[0.2em] text-gold uppercase mb-4" data-id="element-42">
              Start With a Consultation
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif mb-6 text-balance" data-id="element-43">
              Ready to Enter the Dubai Beauty Market?
            </h3>
            <p className="text-charcoal-light text-lg font-light leading-relaxed mb-8" data-id="element-44">
              Before buying or launching a beauty business in Dubai, it is
              important to understand the numbers, risks and opportunities. Book
              a consultation and we will help you evaluate your idea, investment
              budget, business options and next steps.
            </p>

            <div className="space-y-4" data-id="element-45">
              <div className="flex items-center gap-4" data-id="element-46">
                <div className="w-12 h-12 bg-white flex items-center justify-center border border-charcoal/10 rounded-full" data-id="element-47">
                  <span className="font-serif text-xl text-gold" data-id="element-48">1</span>
                </div>
                <p className="font-medium" data-id="element-49">Leave your details</p>
              </div>
              <div className="flex items-center gap-4" data-id="element-50">
                <div className="w-12 h-12 bg-white flex items-center justify-center border border-charcoal/10 rounded-full" data-id="element-51">
                  <span className="font-serif text-xl text-gold" data-id="element-52">2</span>
                </div>
                <p className="font-medium" data-id="element-53">We contact you to discuss goals</p>
              </div>
              <div className="flex items-center gap-4" data-id="element-54">
                <div className="w-12 h-12 bg-white flex items-center justify-center border border-charcoal/10 rounded-full" data-id="element-55">
                  <span className="font-serif text-xl text-gold" data-id="element-56">3</span>
                </div>
                <p className="font-medium" data-id="element-57">Build your strategy</p>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white p-8 md:p-12 shadow-2xl shadow-charcoal/5 lg:my-8 relative" data-id="element-58">
            {status === 'success' ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-59">
                <div className="w-16 h-16 bg-gold/10 text-gold rounded-full flex items-center justify-center mb-6" data-id="element-60">
                  <CheckCircle className="w-8 h-8" data-id="element-61" />
                </div>
                <h4 className="text-3xl font-serif mb-4" data-id="element-62">Request Received</h4>
                <p className="text-charcoal-light font-light mb-6" data-id="element-63">
                  Thank you for reaching out. Our advisory team will contact you
                  shortly to schedule your consultation.
                </p>
                <button onClick={() => setStatus('idle')} className="text-sm text-gold hover:text-gold-hover transition-colors underline" data-id="element-64">
                  Submit another request
                </button>
              </div> : <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-65">
                <h4 className="text-2xl font-serif mb-8 border-b border-charcoal/10 pb-4" data-id="element-66">
                  Request a Consultation
                </h4>

                {/* Error Message */}
                {status === 'error' && errorMessage && <div className="bg-red-50 border border-red-200 rounded p-4 flex items-start gap-3" data-id="element-67">
                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-68" />
                    <p className="text-sm text-red-800" data-id="element-69">{errorMessage}</p>
                  </div>}

                {/* Honeypot field - hidden from users */}
                <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} className="absolute opacity-0 pointer-events-none" tabIndex={-1} autoComplete="off" aria-hidden="true" data-id="element-70" />

                <div className="grid md:grid-cols-2 gap-6" data-id="element-71">
                  <div className="space-y-2" data-id="element-72">
                    <label htmlFor="name" className="text-sm font-medium text-charcoal-light" data-id="element-73">
                      Full Name <span className="text-red-500" data-id="element-74">*</span>
                    </label>
                    <input required type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} disabled={status === 'submitting'} className="w-full border-b border-charcoal/20 py-2 bg-transparent focus:outline-none focus:border-gold transition-colors disabled:opacity-50" placeholder="Jane Doe" data-id="element-75" />
                  </div>
                  <div className="space-y-2" data-id="element-76">
                    <label htmlFor="phone" className="text-sm font-medium text-charcoal-light" data-id="element-77">
                      Phone / WhatsApp <span className="text-red-500" data-id="element-78">*</span>
                    </label>
                    <input required type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} disabled={status === 'submitting'} className="w-full border-b border-charcoal/20 py-2 bg-transparent focus:outline-none focus:border-gold transition-colors disabled:opacity-50" placeholder="+971 50 000 0000" data-id="element-79" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-80">
                  <label htmlFor="email" className="text-sm font-medium text-charcoal-light" data-id="element-81">
                    Email Address <span className="text-red-500" data-id="element-82">*</span>
                  </label>
                  <input required type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} disabled={status === 'submitting'} className="w-full border-b border-charcoal/20 py-2 bg-transparent focus:outline-none focus:border-gold transition-colors disabled:opacity-50" placeholder="jane@example.com" data-id="element-83" />
                </div>

                <div className="space-y-2" data-id="element-84">
                  <label htmlFor="businessType" className="text-sm font-medium text-charcoal-light" data-id="element-85">
                    What type of business are you interested in?{' '}
                    <span className="text-red-500" data-id="element-86">*</span>
                  </label>
                  <select required id="businessType" name="businessType" value={formData.businessType} onChange={handleInputChange} disabled={status === 'submitting'} className="w-full border-b border-charcoal/20 py-2 bg-transparent focus:outline-none focus:border-gold transition-colors text-charcoal disabled:opacity-50" data-id="element-87">
                    <option value="" data-id="element-88">Select an option...</option>
                    <option value="buy_salon" data-id="element-89">
                      Buy an existing salon/clinic
                    </option>
                    <option value="launch_salon" data-id="element-90">
                      Launch a new salon/clinic
                    </option>
                    <option value="spa_wellness" data-id="element-91">Spa & Wellness Center</option>
                    <option value="other" data-id="element-92">Other</option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-93">
                  <label htmlFor="budget" className="text-sm font-medium text-charcoal-light" data-id="element-94">
                    Investment Budget (AED){' '}
                    <span className="text-red-500" data-id="element-95">*</span>
                  </label>
                  <select required id="budget" name="budget" value={formData.budget} onChange={handleInputChange} disabled={status === 'submitting'} className="w-full border-b border-charcoal/20 py-2 bg-transparent focus:outline-none focus:border-gold transition-colors text-charcoal disabled:opacity-50" data-id="element-96">
                    <option value="" data-id="element-97">Select an option...</option>
                    <option value="under_500k" data-id="element-98">Under 500,000 AED</option>
                    <option value="500k_1m" data-id="element-99">500,000 - 1,000,000 AED</option>
                    <option value="1m_3m" data-id="element-100">1,000,000 - 3,000,000 AED</option>
                    <option value="over_3m" data-id="element-101">Over 3,000,000 AED</option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-102">
                  <label htmlFor="message" className="text-sm font-medium text-charcoal-light" data-id="element-103">
                    Message (Optional)
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} disabled={status === 'submitting'} rows={3} className="w-full border-b border-charcoal/20 py-2 bg-transparent focus:outline-none focus:border-gold transition-colors resize-none disabled:opacity-50" placeholder="Tell us more about your goals..." data-id="element-104"></textarea>
                </div>

                <button type="submit" disabled={status === 'submitting'} className="w-full bg-charcoal text-white py-4 text-sm font-medium tracking-wide hover:bg-gold transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-105">
                  {status === 'submitting' ? <>
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-106" />
                      Sending Request...
                    </> : 'Get My Dubai Beauty Business Plan'}
                </button>

                <p className="text-xs text-charcoal-light text-center font-light" data-id="element-107">
                  We respond within 24 hours. Your information is confidential.
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}